<template>
  <div
    v-if="shouldDisplaySidebarItem"
    class="sidebar-item"
    :class="[
      onlyOneChild ? 'no-sub-menu' : 'with-sub-menu',
      { 'sidebar-item--onboarding': isKycRoute },
    ]"
  >
    <SingleSidebarItemOnboarding
      v-if="onlyOneChild && isKycRoute"
      :only-one-child="onlyOneChild"
      :is-nest="isNest"
      :is-kyc-route="isKycRoute"
      :base-path="basePath"
      :is-collapsed="isCollapsed"
    />
    <SingleSidebarItem
      v-else-if="onlyOneChild"
      :only-one-child="onlyOneChild"
      :is-nest="isNest"
      :is-kyc-route="isKycRoute"
      :base-path="basePath"
      :is-collapsed="isCollapsed"
    />
    <el-submenu
      v-else
      ref="subMenu"
      :index="resolvePath(item.path)"
      :popper-append-to-body="true"
      :popper-class="
        classNames({
          'sub-menu': true,
          'sub-menu-onboarding': isKycRoute,
          'is-collapsed': isCollapsed,
        })
      "
      class="sidebar-item__submenu"
    >
      <template slot="title">
        <SidebarItemTooltip :item="item" :is-collapsed="isCollapsed" />
        <span
          v-if="headerLabel && (!isCollapsed || isNest)"
          class="sidebar-item__submenu__title__label"
        >
          {{ headerLabel }}
        </span>
        <div class="" />
        <i
          class="tamara-submenu__icon-arrow el-submenu__icon-arrow el-icon-arrow-down"
          @click.stop="handleArrowClick(resolvePath(item.path), $event)"
        />
        <div v-if="hightLightLabel" class="sidebar-item__hightlight__count">
          {{ hightLightLabel }}
        </div>
      </template>

      <SidebarItem
        v-for="child in visibleChildren"
        :key="child.path + child.name"
        :is-nest="true"
        :item="child"
        :base-path="resolvePath(child.path)"
        :is-collapsed="isCollapsed"
        class="nest-menu"
      />
    </el-submenu>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import classNames from 'classnames'

import FixiOSBug from '@/components/SidebarUtils/FixiOSBug'
import SidebarItemTooltip from '@/components/SidebarUtils/Tooltip.vue'
import SingleSidebarItem from '@/components/SidebarItem/_SingleSidebarItem.vue'
import ResolvePathMixin from '@/mixins/resolve-path'
import { isFunction } from '@/utils/validate'
import SingleSidebarItemOnboarding from '@/components/SidebarItem/_SingleSidebarItemOnboarding.vue'

export default {
  name: 'SidebarItem',
  components: {
    SingleSidebarItemOnboarding,
    SidebarItemTooltip,
    SingleSidebarItem,
  },
  mixins: [FixiOSBug, ResolvePathMixin],
  props: {
    // route object
    item: {
      type: Object,
      required: true,
    },
    isNest: {
      type: Boolean,
      default: false,
    },
    basePath: {
      type: String,
      default: '',
    },
    supportUrl: {
      type: String,
      default: '',
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    showDisputeDot: {
      type: Boolean,
      default: false,
    },
    showOrderDot: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      showLineItem: [
        'tab_kyc-business',
        'tab_kyc-legal',
        'tab_kyc-bank',
        'tab-kyc-additional-info',
      ],
      activeTooltipIndex: null,
    }
  },
  computed: {
    ...mapState('order', ['authorisedOrdersRecords']),
    ...mapState('dispute', [
      'actionRequiredCountSideBar',
      'staticOpenCasesCount',
    ]),
    ...mapGetters('profile', { merchantProfile: 'profile' }),
    shouldDisplayHighlightCount() {
      return ['/orders', '/dispute'].includes(this.basePath)
    },
    orderCount() {
      return this.shouldDisplayHighlightCount &&
        this.authorisedOrdersRecords > 99
        ? '99+'
        : this.authorisedOrdersRecords || null
    },
    disputeCount() {
      return this.shouldDisplayHighlightCount &&
        this.actionRequiredCountSideBar > 99
        ? '99+'
        : this.actionRequiredCountSideBar || null
    },
    hightLightLabel() {
      return this.basePath === '/orders'
        ? this.orderCount
        : this.basePath === '/dispute'
        ? this.disputeCount
        : ''
    },
    shouldDisplaySidebarItem() {
      return this.shouldDisplayItem(this.item?.hidden)
    },
    visibleChildren() {
      return (
        this.item?.children?.filter((item) =>
          this.shouldDisplayItem(item?.hidden)
        ) ?? []
      )
    },
    onlyOneChild() {
      if (!this.item || this.visibleChildren.length > 1) {
        return false
      }

      if (this.visibleChildren.length === 1) {
        return {
          ...this.visibleChildren[0],
          parent: this.item,
        }
      }

      return {
        ...this.item,
        parent: this.item,
        path: '',
        noShowingChildren: true,
      }
    },
    isKycRoute() {
      return this.item.meta && this.item.meta.isKyc
    },
    headerLabel() {
      const { label } = this.item.meta || {}

      return this.$te(label) ? this.$t(label) : label
    },
  },
  methods: {
    classNames,
    resolvePath(routePath) {
      return this.resolvePathWithBasePath(this.basePath, routePath)
    },
    shouldDisplayItem(isHidden) {
      if (isFunction(isHidden)) {
        return !isHidden(this.$store)
      }

      return !isHidden
    },
    handleArrowClick(path, event) {
      this.$emit('click:arrow', this.$refs.subMenu, path, event)
    },
  },
}
</script>

<style lang="scss" scoped>
.sidebar-item {
  // Start define variables ===>>>
  --sidebar-item-width: 100%;
  --sidebar-item-height: 44px;
  --sidebar-item-submenu-width: 100%;
  --sidebar-item-hight-light-count-bg: #8267db;
  --sidebar-item-hight-light-count-color: #fff;
  --sidebar-item-hight-light-margin-start: auto;
  --sidebar-item-hight-light-margin-end: 37px;

  // <<<=== End define variables

  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: var(--sidebar-item-width);
  height: auto;
  min-height: var(--sidebar-item-height);
  &__hightlight__count {
    position: relative;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    margin-inline-start: var(--sidebar-item-hight-light-margin-start);
    margin-inline-end: var(--sidebar-item-hight-light-margin-end);
    font-size: 12px;
    font-weight: 500;
    color: var(--sidebar-item-hight-light-count-color);
    text-align: start;
    background-color: var(--sidebar-item-hight-light-count-bg);
    border-radius: 60px;
  }
  &__submenu {
    width: var(--sidebar-item-submenu-width);
    height: auto;
    margin-inline: 13px;

    &__title__label {
      height: 100%;
      max-height: 30px;
      margin-inline-start: 8px;
      font-family: var(--font-family--inter), sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 30px;
      color: var(--neutral-black-text-500);
    }

    &.is-highlight {
      ::v-deep {
        .el-submenu__title {
          position: relative;

          &::after {
            position: absolute;
            top: calc(50% - 4px);
            right: 44px;
            width: 7px;
            height: 7px;
            content: '';
            background-color: #ab8dff;
            border-radius: 100%;

            [dir='rtl'] & {
              right: unset;
              left: 44px;
            }
          }
        }
      }
    }
  }

  .sidebar-item__submenu {
    ::v-deep {
      .el-submenu__title {
        padding-inline-start: 13px !important;
        color: inherit !important;
        background-color: transparent !important;
        transition: all 0.3s ease-in-out;

        &:hover {
          background-color: #f0e8ff !important;
          border-radius: 5px;
          transition: all 0.3s ease-in;
        }

        [dir='rtl'] & {
          .tamara-submenu__icon-arrow {
            right: unset;
            left: 20px;
          }
        }
      }
    }
  }

  ::v-deep {
    .el-submenu__title {
      display: flex;
      align-items: center;
      width: 100%;
      height: var(--sidebar-item-height);
      padding: 0 !important;
    }

    .el-menu-item {
      height: 100%;
    }
  }
}
</style>
